<template>
    <div class="row">
        <div class="col-lg-12 mb-3">
            <b-breadcrumb :items="breadcrumbItems" class="breadcrumb1 breadcrumbCustom border"/>
            <b-card :header="$t('general.construction.site_instructions.title')" header-tag="h3" class="bg-default-card">
                <data-table
                    v-if="tableLoaded"
                    :exportable="this.$store.getters.hasPermission(['project_site_instructions'], ['read'])"
                    title=""
                    :rows="getData.rowData"
                    :columns="columnData"
                    :perPage="perPage"
                    :rowsCount="rowsCount"
                    :currentPage="currentPage"
                    :pageCount="pageCount"
                    @exportButtonClick="onExportButton"
                    @rowClickDelete="onDeleteRow"
                    @rowClickCheck="onClickCheck"
                    @rowClickEdit="onEditRow"
                    @changePage="changePage"
                    @searchEvent="searchEvent"
                    @orderByEvent="orderByEvent"
                    @rowClickPDF="onPDF('table', ...arguments)"
                    >
                </data-table>

                <b-btn class="btn btn-default mt-2 mr-2" @click="onAddRow" v-if="this.$store.getters.hasPermission(['project_site_instructions'], ['create'])">
                    <i class="fa fa-plus-circle mr-1"></i>{{ $t('general.construction.site_instructions.create_button') }}
                </b-btn>

                <span v-if="this.$store.getters.hasPermission(['project_site_instructions'], ['read']) && getData.rowData.length > 0" @click="onPDF('button', ...arguments)">
                    <label v-if="!this.$store.state.ajax_in_progress" class="btn btn-dark mt-3 text-white">
                        <span><i class="fa fa-file-pdf-o mr-1"></i> {{ $t('general.buttons.pdf_button') + this.currentPage }}</span>
                    </label>
                    <label v-else class="btn btn-dark mt-3 text-white disabled">
                        <span><i class="fa fa-file-pdf-o mr-1"></i> {{ $t('general.ajax_in_progress_please_wait') }}</span>
                    </label>
                </span>
            </b-card>

            <b-modal :ok-title="$t('misc.ok')" :cancel-title="$t('misc.cancel')" ref="saveModal" :title="actionTitle" size="lg" @ok="saveAction" @hidden="saveHidden" :ok-disabled="$store.state.ajax_in_progress || (row && !$store.getters.hasPermission(['project_site_instructions'], ['update']))">
                <div class="row">
                    <div class="col-lg-6">
                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label>{{ $t('table.prepared') }}</label>
                            </div>
                            <div class="col-lg-8">
                                <multiselect v-model="selectedPreparedBy"
                                    :options="currentPreparedBy"
                                    :multiple="false"
                                    :clear-on-select="true"
                                    :hide-selected="false"
                                    :loading="isPreparedByLoading"
                                    :preserve-search="true"
                                    placeholder=""
                                    label="fullname" track-by="id"
                                    :selectLabel="''"
                                    @search-change="onPreparedBySearch"
                                    @input="updatePreparedBy"
                                >
                                    <template slot="clear" slot-scope="props">
                                        <div 
                                            v-if="selectedPreparedBy != null" 
                                            class="multiselect__clear" 
                                            @mousedown.prevent.stop="clearSelectedPreparedBy"
                                        >
                                        </div>
                                    </template>
                                    <span slot="noOptions">{{ $t('modal.general.list_is_empty') }}</span>
                                    <span slot="noResult">{{ $t('modal.general.no_results') }}</span>
                                </multiselect>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label>{{ $t('table.checked') }}</label>
                            </div>
                            <div class="col-lg-8">
                                <multiselect v-model="selectedChecked"
                                    :options="currentChecked"
                                    :multiple="false"
                                    :clear-on-select="true"
                                    :hide-selected="false"
                                    :loading="isCheckedLoading"
                                    :preserve-search="true"
                                    placeholder=""
                                    label="fullname" track-by="id"
                                    :selectLabel="''"
                                    @search-change="onCheckedSearch"
                                    @input="updateChecked"
                                >
                                    <template slot="clear" slot-scope="props">
                                        <div 
                                            v-if="selectedChecked != null" 
                                            class="multiselect__clear" 
                                            @mousedown.prevent.stop="clearSelectedChecked"
                                        >
                                        </div>
                                    </template>
                                    <span slot="noOptions">{{ $t('modal.general.list_is_empty') }}</span>
                                    <span slot="noResult">{{ $t('modal.general.no_results') }}</span>
                                </multiselect>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label for="approved">{{ $t('table.approved') }}</label>
                            </div>
                            <div class="col-lg-8">
                                <multiselect v-model="selectedApproved"
                                    :options="currentApproved"
                                    :multiple="false"
                                    :clear-on-select="true"
                                    :hide-selected="false"
                                    :loading="isApprovedLoading"
                                    :preserve-search="true"
                                    placeholder=""
                                    label="fullname" track-by="id"
                                    :selectLabel="''"
                                    @search-change="onApprovedSearch"
                                    @input="updateApproved"
                                >
                                    <template slot="clear" slot-scope="props">
                                        <div 
                                            v-if="selectedApproved != null" 
                                            class="multiselect__clear" 
                                            @mousedown.prevent.stop="clearSelectedApproved"
                                        >
                                        </div>
                                    </template>
                                    <span slot="noOptions">{{ $t('modal.general.list_is_empty') }}</span>
                                    <span slot="noResult">{{ $t('modal.general.no_results') }}</span>
                                </multiselect>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label>{{ $t('table.site_instruction_number') }}</label>
                            </div>
                            <div class="col-lg-8 mt-2">
                                <input type="text" maxlength="50" class="form-control" v-model="model.site_instruction_number">
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label>{{ $t('table.date_issued') }}</label>
                            </div>
                            <div class="col-lg-8">
                                <flat-pickr :config="{enableTime: false, inline: false}" class="form-control bg-white" v-model="model.date"></flat-pickr>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label>{{ $t('misc.title') }}</label>
                            </div>
                            <div class="col-lg-8">
                                <input type="text" maxlength="50" class="form-control" v-model="model.title"/>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label>{{ $t('misc.area') }}</label>
                            </div>
                            <div class="col-lg-8">
                                <input type="text" maxlength="50" class="form-control" v-model="model.area"/>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label>{{ $t('table.required_completion_date') }}</label>
                            </div>
                            <div class="col-lg-8 mt-2">
                                <flat-pickr :config="{enableTime: false, inline: false}" class="form-control bg-white" v-model="model.required_completion_date"></flat-pickr>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label for="additional_cost">{{ $t('table.additional_cost') }}</label>
                            </div>
                            <div class="col-lg-8 mt-2" style="z-index: 0">
                                <toggle-button v-model="model.additional_cost" :sync="true" :value="false" :labels="{checked: $t('misc.yes'), unchecked: $t('misc.no')}"/>
                            </div>
                        </div>

                        <div class="row mb-3" v-if="model.additional_cost == 1">
                            <div class="col-lg-4">
                                <label>{{ $t('table.change_order') }}</label>
                            </div>
                            <div class="col-lg-8">
                                <multiselect v-model="selectedChangeOrder"
                                    :options="currentChangeOrder"
                                    :multiple="false"
                                    :clear-on-select="true"
                                    :hide-selected="false"
                                    :loading="isChangeOrderLoading"
                                    :preserve-search="true"
                                    placeholder=""
                                    label="site_change_number" track-by="id"
                                    :selectLabel="''"
                                    @search-change="onChangeOrderSearch"
                                    @input="updateChangeOrder"
                                >
                                    <template slot="clear" slot-scope="props">
                                        <div 
                                            v-if="selectedChangeOrder != null" 
                                            class="multiselect__clear" 
                                            @mousedown.prevent.stop="clearSelectedChangeOrder"
                                        >
                                        </div>
                                    </template>
                                    <span slot="noOptions">{{ $t('modal.general.list_is_empty') }}</span>
                                    <span slot="noResult">{{ $t('modal.general.no_results') }}</span>
                                </multiselect>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label for="execution_by_company">{{ $t('table.execution_by_company') }}</label>
                            </div>
                            <div class="col-lg-8">
                                <multiselect v-model="selectedCompany"
                                    :options="currentCompany"
                                    :multiple="false"
                                    :clear-on-select="true"
                                    :hide-selected="false"
                                    :loading="isCompanyLoading"
                                    :preserve-search="true"
                                    placeholder=""
                                    label="name" track-by="id"
                                    :selectLabel="''"
                                    @search-change="onCompanySearch"
                                    @input="updateCompany"
                                >
                                    <template slot="clear" slot-scope="props">
                                        <div 
                                            v-if="selectedCompany != null" 
                                            class="multiselect__clear" 
                                            @mousedown.prevent.stop="clearSelectedCompany"
                                        >
                                        </div>
                                    </template>
                                    <span slot="noOptions">{{ $t('modal.general.list_is_empty') }}</span>
                                    <span slot="noResult">{{ $t('modal.general.no_results') }}</span>
                                </multiselect>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label for="released_for_construction">{{ $t('table.released_for_construction') }}</label>
                            </div>
                            <div class="col-lg-8 mt-2" style="z-index: 0">
                                <toggle-button v-model="model.released_for_construction" :sync="true" :value="false" :labels="{checked: $t('misc.yes'), unchecked: $t('misc.no')}"/>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label for="working_group">{{ $t('table.working_group') }}</label>
                            </div>
                            <div class="col-lg-8">
                                <multiselect v-model="selectedWorkingGroupTeam"
                                    :options="currentWorkingGroupTeam"
                                    :multiple="false"
                                    :clear-on-select="true"
                                    :hide-selected="false"
                                    :loading="isWorkingGroupTeamLoading"
                                    :preserve-search="true"
                                    placeholder=""
                                    label="name" track-by="id"
                                    :selectLabel="''"
                                    @search-change="onWorkingGroupTeamSearch"
                                    @input="updateWorkingGroupTeam"
                                >
                                    <template slot="clear" slot-scope="props">
                                        <div 
                                            v-if="selectedWorkingGroupTeam != null" 
                                            class="multiselect__clear" 
                                            @mousedown.prevent.stop="clearSelectedWorkingGroupTeam"
                                        >
                                        </div>
                                    </template>
                                    <span slot="noOptions">{{ $t('modal.general.list_is_empty') }}</span>
                                    <span slot="noResult">{{ $t('modal.general.no_results') }}</span>
                                </multiselect>
                            </div>
                        </div>

                         <div class="row mb-3" v-if="this.model.working_group_id != null">
                            <div class="col-lg-4">
                                <label>{{ $t('table.team_member') }}</label>
                            </div>
                            <div class="col-lg-8">
                                <multiselect v-model="selectedUser"
                                    :options="currentUser"
                                    :multiple="false"
                                    :clear-on-select="true"
                                    :hide-selected="false"
                                    :loading="isUserLoading"
                                    :preserve-search="true"
                                    placeholder=""
                                    label="fullname" track-by="id"
                                    :selectLabel="''"
                                    @search-change="onUserSearch"
                                    @input="updateUser"
                                >
                                    <template slot="clear" slot-scope="props">
                                        <div 
                                            v-if="selectedUser != null" 
                                            class="multiselect__clear" 
                                            @mousedown.prevent.stop="clearSelectedUser"
                                        >
                                        </div>
                                    </template>
                                    <span slot="noOptions">{{ $t('modal.general.list_is_empty') }}</span>
                                    <span slot="noResult">{{ $t('modal.general.no_results') }}</span>
                                </multiselect>
                            </div>
                        </div>
                    </div>

                    <div class="col-lg-6">
                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label for="execution_performed">{{ $t('table.execution_performed') }}</label>
                            </div>
                            <div class="col-lg-8 mt-2" style="z-index: 0">
                                <toggle-button v-model="model.execution_performed" :sync="true" :value="false" :labels="{checked: $t('misc.yes'), unchecked: $t('misc.no')}"/>
                            </div>
                        </div>

                        <div class="row mb-3" v-if="model.execution_performed == 1">
                            <div class="col-lg-4">
                                <label for="execution_performed_date">{{ $t('table.execution_performed_date') }}</label>
                            </div>
                            <div class="col-lg-8 mt-2">
                                <flat-pickr :config="{enableTime: false, inline: false}" class="form-control bg-white" v-model="model.execution_performed_date"></flat-pickr>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label for="client_confirmed">{{ $t('table.client_confirmed') }}</label>
                            </div>
                            <div class="col-lg-8 mt-2" style="z-index: 0">
                                <toggle-button v-model="model.client_confirmed" :sync="true" :value="false" :labels="{checked: $t('misc.yes'), unchecked: $t('misc.no')}"/>
                            </div>
                        </div>

                        <div class="row mb-3" v-if="model.client_confirmed == 1">
                            <div class="col-lg-4">
                                <label for="confirmed_client_date">{{ $t('table.confirmed_client_date') }}</label>
                            </div>
                            <div class="col-lg-8 mt-2">
                                <flat-pickr :config="{enableTime: false, inline: false}" class="form-control bg-white" v-model="model.confirmed_client_date"></flat-pickr>
                            </div>
                        </div>

                        <div v-if="model.client_confirmed==1">
                            <div class="row mb-3">
                                <div class="col-lg-4">
                                    <label for="cost_send_client">{{ $t('table.cost_send_client') }}</label>
                                </div>
                                <div class="col-lg-8 mt-2" style="z-index: 0">
                                    <toggle-button v-model="model.cost_send_client" :sync="true" :value="false" :labels="{checked: $t('misc.yes'), unchecked: $t('misc.no')}"/>
                                </div>
                            </div>

                            <div class="row mb-3" v-if="model.cost_send_client == 1">
                                <div class="col-lg-4">
                                    <label for="cost_send_client_date">{{ $t('table.cost_send_client_date') }}</label>
                                </div>
                                <div class="col-lg-8 mt-2">
                                    <flat-pickr :config="{enableTime: false, inline: false}" class="form-control bg-white" v-model="model.cost_send_client_date"></flat-pickr>
                                </div>
                            </div>

                            <div v-if="model.cost_send_client == 1">
                                <div class="row mb-3">
                                    <div class="col-lg-4">
                                        <label for="client_confirmed_cost">{{ $t('table.client_confirmed_cost') }}</label>
                                    </div>
                                    <div class="col-lg-8 mt-3" style="z-index: 0">
                                        <toggle-button v-model="model.client_confirmed_cost" :sync="true" :value="false" :labels="{checked: $t('misc.yes'), unchecked: $t('misc.no')}"/>
                                    </div>
                                </div>

                                <div class="row mb-3" v-if="model.client_confirmed_cost == 1">
                                    <div class="col-lg-4">
                                        <label for="client_confirmed_cost_date">{{ $t('table.client_confirmed_cost_date') }}</label>
                                    </div>
                                    <div class="col-lg-8 mt-2">
                                        <flat-pickr :config="{enableTime: false, inline: false}" class="form-control bg-white" v-model="model.client_confirmed_cost_date"></flat-pickr>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-6">
                                <label>{{ $t('table.signature_1') }}</label>
                            </div>
                            <div class="col-lg-6">
                                <toggle-button v-model="model.signature_id_1" :sync="true" :width="100" :value="false" :labels="{checked: $t('misc.signed'), unchecked: $t('misc.not_signed')}" :color="toggleColor1"/>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-6">
                                <label>{{ $t('table.signature_2') }}</label>
                            </div>
                            <div class="col-lg-6">
                                <toggle-button v-model="model.signature_id_2" :sync="true" :width="100" :value="false" :labels="{checked: $t('misc.signed'), unchecked: $t('misc.not_signed')}" :color="toggleColor2"/>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-6">
                                <label>{{ $t('table.signature_3') }}</label>
                            </div>
                            <div class="col-lg-6">
                                <toggle-button v-model="model.signature_id_3" :sync="true" :width="100" :value="false" :labels="{checked: $t('misc.signed'), unchecked: $t('misc.not_signed')}" :color="toggleColor3"/>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label for="remarks">{{ $t('table.remarks') }}</label>
                            </div>
                            <div class="col-lg-8">
                                <textarea v-model="model.remarks" rows="4" class="form-control resize_vertical" name="remarks" id="remarks" required></textarea>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-lg-12">
                        <label for="description">{{ $t('table.description') }}</label>
                        <textarea rows="6" maxlength="5000" class="form-control resize_vertical" v-model="model.description"></textarea>
                    </div>
                </div>

                <hr v-if="(getData.getFiles.length > 0 && hasFilesReadPermission) || hasFilesCreatePermission">
                <div class="row mt-2" v-if="getData.getFiles.length > 0 && hasFilesReadPermission">
                    <div class="col-lg-12">
                        <label>{{ $t('table.uploaded_files') }}</label>
                    </div>
                    <div class="col-lg-6" v-for="file in getData.getFiles" :key="file.id">
                        <div class="card p-2">
                            <div style="width: 100%; display: block">
                                <p 
                                    class="float-left customLink" 
                                    @click="onDownloadFile(file.id)" 
                                    :title="file.file_name"
                                >
                                    {{file.file_name}}
                                </p>
                                <p 
                                    v-if="hasFilesDeletePermission"
                                    class="float-right p-0 m-0"
                                >
                                    <i class='fa fa-2x fa-trash mt-2 text-danger sortable cursor-pointer' @click="onDeleteFile(file.id)"></i>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row mt-2" v-if="hasFilesCreatePermission">
                    <div class="col-lg-12">
                        <div class="row mb-3">
                            <div class="col-lg-12">
                                <label v-if="row && getData.getFiles.length > 0">{{ $t('table.upload_more_files') }}</label>
                                <label v-else>{{ $t('table.upload_files') }}</label>
                            </div>
                            <div class="col-lg-12">
                                <vue-dropzone class="dropzone" ref="document" id="dropzone" :options="dropzoneOptions"></vue-dropzone>
                            </div>
                        </div>
                    </div>
                </div>
            </b-modal>

            <confirm-dialog ref="deleteModal" :title="actionTitleDelete" @buttonClick="deleteAction">
                {{deleteMessage}}
            </confirm-dialog>

            <confirm-dialog ref="pdfModal" :title="actionTitlePdf" @buttonClick="onDownloadPDF">
                <p class="m-0">{{ download_pdf_message }}</p>
            </confirm-dialog>
        </div>
    </div>
</template>
<script>
import Vue from 'vue';
import VueForm from "vue-form";
import DataTable from '@/components/global/tables/datatables/DataTable.vue';
import vue2Dropzone from 'vue2-dropzone';
import '@/assets/css/vue2Dropzone.css';
import ConfirmDialog from '@/components/global/modals/ConfirmDialog.vue';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import * as notificationIcons from '@/components/global/miscellaneous/notificationIcons.js';
import http from '@/libs/http';
import miniToastr from 'mini-toastr';
import ToggleButton from 'vue-js-toggle-button';
import Multiselect from 'vue-multiselect';

export default {
    name: 'site_instructions',
    components: {
        Multiselect,
        DataTable,
        flatPickr,
        ConfirmDialog,
        notificationIcons,
        vueDropzone: vue2Dropzone,
    },
    data() {
        return {
            breadcrumbItems: [
                {
                    text: Vue.i18n.translate('general.dashboard.title'),
                    to: '/',
                },
                {
                    text: Vue.i18n.translate('general.projects.title_modal') + ': ' + this.$store.state.current_project.name,
                    to: '/project/'+this.$route.params.id,
                },
                {
                    text: Vue.i18n.translate('general.construction.title'),
                    active: true
                },
                {
                    text: Vue.i18n.translate('general.construction.site_instructions.title'),
                    active: true
                }
            ],
            tableName: 'siteinstruction',
            tableNameTranslated: Vue.i18n.translate('general.construction.site_instructions.title_modal'),
            row: null,
            tableLoaded: false,
            perPage: 0,
            rowsCount: 0,
            currentPage: 1,
            sortType: 'asc',
            sortBy: 'id',
            searchInput: '',

            actionTitle: '',
            actionTitleDelete: '',
            actionTitlePdf: '',

            deleteMessage: '',
            download_pdf_message: '',
            pdfType: null,

            selectedPreparedBy: null,
            currentPreparedBy: [],
            isPreparedByLoading: false,

            selectedChecked: null,
            currentChecked: [],
            isCheckedLoading: false,

            selectedApproved: null,
            currentApproved: [],
            isApprovedLoading: false,

			selectedChangeOrder: null,
			currentChangeOrder: [],
			isChangeOrderLoading: false,

            selectedCompany: null,
            currentCompany: [],
            isCompanyLoading: false,

			selectedWorkingGroupTeam: null,
			currentWorkingGroupTeam: [],
			isWorkingGroupTeamLoading: false,

            selectedUser: null,
            currentUser: [],
            isUserLoading: false,

            toggleColor1: '#5373d4',
            toggleColor2: '#5373d4',
            toggleColor3: '#5373d4',

            file_id: null,
            isFileDelete: false,

            hasFilesReadPermission: false,
            hasFilesCreatePermission: false,
            hasFilesDeletePermission: false,

            dropzoneOptions: {
                url: '/',
                thumbnailWidth: 150,
                addRemoveLinks: true,
                autoProcessQueue: false,
                uploadMultiple: true,
                dictDefaultMessage: '<i class="fa fa-cloud-upload"></i>' + ' ' + Vue.i18n.translate('table.drag_to_upload'),
                maxFiles: 3,
            },
            getData: {
                rowData: [],
                getFiles: [],
            },
            model: {
                prepared_id: null,
                checked_id: null,
                approved_id: null,
                change_order_id: null,
                site_instruction_number: null,
                date: null,
                area: null,
                required_completion_date: null,
                additional_cost: null,
                title: null,
                description: null,
                remarks: null,
                execution_by_company: null,
                released_for_construction: null,
                working_group_id: null,
                user_id: null,
                execution_performed: null,
                execution_performed_date: null,
                client_confirmed: null,
                confirmed_client_date: null,
                cost_send_client: null,
                cost_send_client_date: null,
                client_confirmed_cost: null,
                client_confirmed_cost_date: null,
                file_id_1: null,
                signature_id_1: null,
                signature_id_2: null,
                signature_id_3: null
            },
            columnData: [
                {
                    label: Vue.i18n.translate('misc.actions'),
                    width: '125px',
                    field: '',
                    showEditButton: true,
                    showDeleteButton: true,
                    showPDF: true,
                },
                {
                    label: Vue.i18n.translate('table.site_instruction_number'),
                    field: 'site_instruction_number',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.date_issued'),
                    field: 'date',
                    width: '110px',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.required_completion_date'),
                    field: 'required_completion_date',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('misc.area'),
                    field: 'area',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.additional_cost'),
                    field: 'additional_cost',
                    numeric: false,
                    html: false,
                    showAsCheck: true,
                },
                {
                    label: Vue.i18n.translate('table.execution_by_company'),
                    field: 'company.name',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.released_for_construction'),
                    field: 'released_for_construction',
                    numeric: false,
                    html: false,
                    showAsCheck: true,
                },
                {
                    label: Vue.i18n.translate('table.execution_performed'),
                    field: 'execution_performed',
                    numeric: false,
                    html: false,
                    showAsCheck: true,
                },
                {
                    label: Vue.i18n.translate('table.client_confirmed'),
                    field: 'client_confirmed',
                    numeric: false,
                    html: false,
                    showAsCheck: true,
                },
                {
                    label: Vue.i18n.translate('table.cost_send_client'),
                    field: 'cost_send_client',
                    numeric: false,
                    html: false,
                    showAsCheck: true,
                },
                {
                    label: Vue.i18n.translate('table.client_confirmed_cost'),
                    field: 'client_confirmed_cost',
                    numeric: false,
                    html: false,
                    showAsCheck: true,
                },
                {
                    label: Vue.i18n.translate('table.working_group'),
                    width: '130px',
                    field: 'working_group.name',
                    numeric: false,
                    html: false,
                }
            ],
        }
    },
    mounted () {
        if (this.$store.state.go_back_url == 'change_orders') {
            this.onLoadTable().then(res => {
                let row = this.getData.rowData.find(r => r.id == this.$store.state.go_back_data_list[0]);

                if(row != null) {
                    this.onEditRow(row);
                }
            });
        }

        this.onLoadTable();

        this.hasFilesReadPermission   = this.$store.getters.hasPermission(['files'], ['read']);
        this.hasFilesCreatePermission = this.$store.getters.hasPermission(['files'], ['create']);
        this.hasFilesDeletePermission = this.$store.getters.hasPermission(['files'], ['delete']);
    },
    methods: {
        errorMsg(msg) {
            miniToastr.error(msg, Vue.i18n.translate('messages.error'), 5000);
        },
        successMsg(msg) {
            miniToastr.success(msg, Vue.i18n.translate('messages.success'), 5000);
        },
        orderByEvent (column, sort_type) {
            this.sortType = sort_type;
            this.sortBy = column.field;
            this.onLoadTable();
        },
        searchEvent (searchInput) {
            this.searchInput = searchInput;
            this.onLoadTable();
        },
        changePage (page) {
            this.currentPage = page;
            this.onLoadTable();
        },
        async onLoadTable () {
            return new Promise((resolve, reject) => {
                let data = http.route(this.tableName + '?project_id=' + this.$route.params.id + '&search=' + this.searchInput + '&page=' + this.currentPage + '&sort_by=' + this.sortBy + '&sort_order=' + this.sortType).get();
                data.then(response => {
                    if (!response.isError) {
                        this.perPage = response.pagination.limit;
                        this.rowsCount = response.pagination.totalEntries;
                        this.currentPage = response.pagination.currentPage;
                        this.pageCount = response.pagination.totalPages;
                        var data = response.data;
                        data.forEach(field => {
                            if (field.working_group == null) {
                                field.working_group = { 'name': '' }
                            }

                            if (field.company == null) {
                                field.company = { 'name': '' }
                            }
                        });
                        this.getData.rowData = data;
                        this.tableLoaded = true;
                    } else {
                        Object.keys(response.errorMessage).forEach(key => {
                            this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                        });
                    }
                    resolve();
                });
            });
        },
        clearSearches () {
            this.selectedPreparedBy       = null;
            this.selectedChecked          = null;
            this.selectedApproved         = null;
            this.selectedChangeOrder      = null;
            this.selectedCompany          = null;
            this.selectedWorkingGroupTeam = null;
            this.selectedUser             = null;

            this.currentPreparedBy       = [];
            this.currentChecked          = [];
            this.currentApproved         = [];
            this.currentChangeOrder      = [];
            this.currentCompany          = [];
            this.currentWorkingGroupTeam = [];
            this.currentUser             = [];

            this.getData.getFiles = [];
            this.$nextTick(() => {
                if (typeof this.$refs.document != 'undefined') {
                    this.$refs.document.removeAllFiles();
                }
            });
        },
        onAddRow () {
            this.actionTitle = Vue.i18n.translate('modal.general.create') + ' ' + this.tableNameTranslated;
            this.row = null;
            this.clearSearches();

            this.toggleColor1 = '#5373d4';
            this.toggleColor2 = '#5373d4';
            this.toggleColor3 = '#5373d4';
            this.model.prepared = null;
            this.model.checked = null;
            this.model.approved = null;
            this.model.site_instruction_number = null;
            this.model.date = null;
            this.model.area = null;
            this.model.required_completion_date = null;
            this.model.additional_cost = false;
            this.model.title = null;
            this.model.description = null;
            this.model.remarks = null;
            this.model.execution_by_company = null;
            this.model.released_for_construction = false;
            this.model.working_group_id = null;
            this.model.user_id = null;
            this.model.execution_performed = null;
            this.model.execution_performed_date = null;
            this.model.client_confirmed = null;
            this.model.confirmed_client_date = null;
            this.model.cost_send_client = null;
            this.model.cost_send_client_date = null;
            this.model.client_confirmed_cost = null;
            this.model.client_confirmed_cost_date = null;
            this.model.signature_id_1 = null;
            this.model.signature_id_2 = null;
            this.model.signature_id_3 = null;

            this.$refs.saveModal.show();
        },
        onEditRow (row) {
            this.actionTitle = Vue.i18n.translate('modal.general.edit') + ' ' + this.tableNameTranslated;
            this.row = row;
            this.clearSearches();

            if (this.hasFilesReadPermission) {
                this.onViewFiles(row.id);
            }

            this.toggleColor1 = ((this.$store.state.user.user.id == this.row.signature_id_1) || this.row.signature_id_1 == null) ? '#5373d4' : '#75C791';
            this.toggleColor2 = ((this.$store.state.user.user.id == this.row.signature_id_2) || this.row.signature_id_2 == null) ? '#5373d4' : '#75C791';
            this.toggleColor3 = ((this.$store.state.user.user.id == this.row.signature_id_3) || this.row.signature_id_3 == null) ? '#5373d4' : '#75C791';
            this.model.prepared = this.row.prepared;
            this.model.checked = this.row.checked;
            this.model.approved = this.row.approved;
            this.model.site_instruction_number = this.row.site_instruction_number;
            this.model.date = this.row.date;
            this.model.area = this.row.area;
            this.model.required_completion_date = this.row.required_completion_date;
            this.model.additional_cost = Boolean(this.row.additional_cost);
            this.model.title = this.row.title;
            this.model.description = this.row.description;
            this.model.remarks = this.row.remarks;
            this.model.released_for_construction = Boolean(this.row.released_for_construction);
            this.model.execution_by_company = this.row.company_id;
            this.model.prepared_id = this.row.prepared_id;
            this.model.checked_id = this.row.checked_id;
            this.model.approved_id = this.row.approved_id;
            this.model.change_order_id = this.row.change_order_id;
            this.model.file_id_1 = this.row.file_id_1;
            this.model.working_group_id = this.row.working_group_id;
            this.model.user_id = this.row.user_id;

            this.selectedPreparedBy       = this.row.prepared_by;
            this.selectedChecked          = this.row.checked;
            this.selectedApproved         = this.row.approved;
            this.selectedChangeOrder      = this.row.change_order;
            this.selectedCompany          = (row.company != null && typeof row.company.name != 'undefined' && row.company.name != '') ? row.company : null;
            this.selectedWorkingGroupTeam = (row.working_group != null && typeof row.working_group.name != 'undefined' && row.working_group.name != '') ? row.working_group : null;
            this.selectedUser             = this.row.user;

            this.model.execution_performed = Boolean(this.row.execution_performed);
            this.model.execution_performed_date = this.row.execution_performed_date;
            this.model.client_confirmed = Boolean(this.row.client_confirmed);
            this.model.confirmed_client_date = this.row.confirmed_client_date;
            this.model.cost_send_client = Boolean(this.row.cost_send_client);
            this.model.cost_send_client_date = this.row.cost_send_client_date;
            this.model.client_confirmed_cost = Boolean(this.row.client_confirmed_cost);
            this.model.client_confirmed_cost_date = this.row.client_confirmed_cost_date;
            this.model.signature_id_1 = Boolean(this.row.signature_id_1);
            this.model.signature_id_2 = Boolean(this.row.signature_id_2);
            this.model.signature_id_3 = Boolean(this.row.signature_id_3);

            this.$refs.saveModal.show();
        },
        saveAction (e) {
            e.preventDefault();

            let put_data = {
                project_id:                 this.$route.params.id,
                prepared_id:                this.model.prepared_id,
                checked_id:                 this.model.checked_id,
                approved_id:                this.model.approved_id,
                change_order_id:            this.model.change_order_id,
                site_instruction_number:    this.model.site_instruction_number,
                date:                       this.model.date,
                area:                       this.model.area,
                required_completion_date:   this.model.required_completion_date,
                additional_cost:            this.model.additional_cost,
                title:                      this.model.title,
                description:                this.model.description,
                remarks:                    this.model.remarks,
                company_id:                 this.model.execution_by_company,
                released_for_construction:  this.model.released_for_construction,
                working_group_id:           this.model.working_group_id,
                user_id:                    this.model.user_id,
                execution_performed:        this.model.execution_performed,
                execution_performed_date:   this.model.execution_performed_date,
                client_confirmed:           this.model.client_confirmed,
                confirmed_client_date:      this.model.confirmed_client_date,
                cost_send_client:           this.model.cost_send_client,
                cost_send_client_date:      this.model.cost_send_client_date,
                client_confirmed_cost:      this.model.client_confirmed_cost,
                client_confirmed_cost_date: this.model.client_confirmed_cost_date,
                file_id_1:                  this.model.file_id_1,
                signature_id_1:             this.model.signature_id_1,
                signature_id_2:             this.model.signature_id_2,
                signature_id_3:             this.model.signature_id_3,
            };

            Object.keys(put_data).forEach(key => {
                if (put_data[key] === '') {
                    put_data[key] = null;
                }
            });

            if (this.row) {
                http.route(this.tableName + '/' + this.row.id).patch(put_data, true).then(response => {
                    if(response.data == false) {
                        Object.keys(response.errorMessage).forEach(key => {
                            this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                        });
                    } else {
                        this.onLoadTable();
                        this.uploadDocuments(this.row.id);
                        this.$refs.saveModal.hide();
                        this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.updated'));
                    }
                });
            } else {
                http.route(this.tableName).post(put_data, true).then(response => {
                    if(response.data == false) {
                        Object.keys(response.errorMessage).forEach(key => {
                            this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                        });
                    } else {
                        this.onLoadTable();
                        this.uploadDocuments(response.data.id);
                        this.$refs.saveModal.hide();
                        this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.created'));
                    }
                });
            }
        },
        uploadDocuments (id) {
            if (this.hasFilesCreatePermission && typeof this.$refs.document != 'undefined') {
                var file = this.$refs.document.getQueuedFiles();
                for (let order = 0; order < file.length; order++) {
                    var formData = new FormData();
                    formData.append('file', file[order]);
                    formData.append('type', 'site_instruction');
                    formData.append('site_instruction_id', id);
                    formData.append('project_id', this.$route.params.id);
                    let data = http.route('file').post(
                        formData,
                            {
                            headers: {
                                'Content-Type': 'multipart/form-data'
                            }
                        }
                    ).then(response => {
                        if(response.data == false) {
                            Object.keys(response.errorMessage).forEach(key => {
                                this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                            });
                        } else {
                            this.successMsg(Vue.i18n.translate('messages.file_uploaded'));
                        }
                    });
                }
            }
        },
        onViewFiles (id) {
            this.getData.getFiles.length = 0;
            let data = http.route('file?filters[site_instruction_id]=' + id).get();
            data.then(response => {
                if(response.data == false) {
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                    });
                } else {
                    this.getData.getFiles = response.data;
                }
            });
        },
        onDownloadFile (id) {
            let data = http.route('file/' + id).get(
            {
                export_pdf: 1
            }, true);
            data.then(response => {
                if (!response.isError) {
                    this.successMsg(Vue.i18n.translate('messages.file.file_download'));
                    let data = http.route('get_content/file/'+response.data.id+'/'+response.data.file_name).downloadPdf({}, response.data.file_name).then(res => {});
                } else {
                    this.errorMsg(Vue.i18n.translate('messages.file.error_download'));
                }
            });
        },
        onDeleteRow (row, index) {
            this.actionTitleDelete = Vue.i18n.translate('modal.general.delete') + ' ' + this.tableNameTranslated;
            this.deleteMessage = Vue.i18n.translate('messages.delete');
            this.row = row;
            this.file_id = null;
            this.isFileDelete = false;
            this.$refs.deleteModal.show();
        },
        onDeleteFile (id) {
            this.actionTitleDelete = Vue.i18n.translate('messages.delete_file_title');
            this.deleteMessage = Vue.i18n.translate('messages.delete_file');
            this.file_id = id;
            this.isFileDelete = true;
            this.$refs.deleteModal.show();
        },
        deleteAction (button) {
            if (button == 1) {
                if (this.isFileDelete == true) {
                    let data = http.route('file/' + this.file_id).delete();
                    data.then(response => {
                        if (!response.isError) {
                            this.onLoadTable();
                            this.successMsg(Vue.i18n.translate('messages.deleted_file_message'));
                            this.file_id = null;
                            this.$refs.saveModal.hide();
                        } else {
                            Object.keys(response.errorMessage).forEach(key => {
                                this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                            });
                        }
                    });
                } else if (this.isFileDelete == false) {
                    let data = http.route(this.tableName + '/' + this.row.id).delete();
                    data.then(response => {
                        if (!response.isError) {
                            if (this.currentPage > 1 && this.getData.rowData.length == 1) {
                                this.currentPage = this.currentPage - 1;
                            }

                            this.onLoadTable();
                            this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.deleted'));
                        } else {
                            this.errorMsg(Vue.i18n.translate('errors.error_delete'));
                        }
                    });
                }
            }
            this.$refs.deleteModal.hide();
        },
        onClickCheck (row, index, column) {
            if (row) {
                this.model[column.toString()] = (row[column.toString()] == null || row[column.toString()] == 0) ? 1 : 0;

                var clone = Object.assign({}, row);
                clone[column.toString()] = !clone[column.toString()];

                let data = http.route(this.tableName + '/' + row.id).patch(clone, true);
                data.then(response => {
                    if(response.data == false) {
                        Object.keys(response.errorMessage).forEach(key => {
                            this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                        });
                    } else {
                        row[column.toString()] = !row[column.toString()];
                        this.onLoadTable();
                        this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.updated'));
                    }
                });
                this.model[column.toString()] = null;
            }
        },
        onPDF (type, row) {
            this.pdfType = type;
            switch(this.pdfType) {
                case 'table':   
                    this.actionTitlePdf = Vue.i18n.translate('modal.general.download_pdf');
                    this.download_pdf_message = Vue.i18n.translate('messages.download_pdf');
                    break;
                case 'button':
                    this.actionTitlePdf = Vue.i18n.translate('modal.general.download_pdfs');
                    this.download_pdf_message = Vue.i18n.translate('messages.download_pdfs');
                    break;
            }
            this.row = row;
            this.$refs.pdfModal.show();
        },
        onDownloadPDF (button) {
            if(button == 1) {
                switch(this.pdfType) {
                    case 'table':
                        let data = http.route(this.tableName + '/' + this.row.id).get(
                        {
                            export_pdf: 1
                        }, true);
                        data.then(response => {
                            if (!response.isError) {
                                let data = http.route('get_content/'+response.data.type+'/'+response.data.id+'/'+response.data.name).downloadPdf({}, response.data.name).then(res => {
                                    if (typeof res == 'undefined') {
                                        miniToastr.success(Vue.i18n.translate('messages.pdf_generated'), Vue.i18n.translate('messages.success'));
                                    } else {
                                        Object.keys(res.errorMessage).forEach(key => {
                                            this.errorMsg(Vue.i18n.translate(res.errorMessage[key]));
                                        });
                                    }
                                });
                            } else {
                                miniToastr.error(Vue.i18n.translate('errors.error_generating_pdf'), Vue.i18n.translate('messages.error'));
                            }
                        });
                        break;
                    case 'button':
                        this.getData.rowData.forEach(row => {
                            let data = http.route(this.tableName + '/' + row.id).get(
                            {
                                export_pdf: 1
                            }, true);
                            data.then(response => {
                                if (!response.isError) {
                                    let data = http.route('get_content/'+response.data.type+'/'+response.data.id+'/'+response.data.name).downloadPdf({}, response.data.name).then(res => {
                                        if (typeof res == 'undefined') {
                                            miniToastr.success(Vue.i18n.translate('messages.pdf_generated'), Vue.i18n.translate('messages.success'));
                                        } else {
                                            Object.keys(res.errorMessage).forEach(key => {
                                                this.errorMsg(Vue.i18n.translate(res.errorMessage[key]));
                                            });
                                        }
                                    });
                                } else {
                                    miniToastr.error(Vue.i18n.translate('errors.error_generating_pdf'), Vue.i18n.translate('messages.error'));
                                }
                            });
                        })
                        break;
                }
            }

            this.$refs.pdfModal.hide();
        },
        onExportButton() {
            http.route(this.tableName + '?project_id=' + this.$route.params.id).exportExcel({
                export: 1,
                search: this.searchInput,
                sort_by: this.sortBy,
                sort_order: this.sortType
            }, 'site_instructions.xlsx').then(response => {
                if (typeof response != 'undefined') {
                    this.errorMsg(Vue.i18n.translate('errors.error_export'));
                } else {
                    this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.exported'));
                }
            });
        },
        saveHidden() {
            if (this.$store.state.go_back_url == 'change_orders') {
                this.$store.state.go_back_url = 'site_instructions';
                this.$router.push({name: 'change_orders', params: {id:this.$route.params.id}});
            }
        },
        onPreparedBySearch(search) {
            if (search == '') {
                this.currentPreparedBy = [];
                
                return false;
            }

            this.isPreparedByLoading = true;
            this.searchPreparedBy(search, this);
        },
        searchPreparedBy: _.debounce((search, vm) => {
            let data = http.route('user/getall?filters[project_id]='+vm.$route.params.id+'&search_select=' + search).get();
            data.then(response => {
                if (response.data != false) {
                    vm.currentPreparedBy = response.data;
                }
                vm.isPreparedByLoading = false;
            });
        }, 500),
        updatePreparedBy(user) {
            this.model.prepared_id = (user != null) ? user.id : null;
        },
        clearSelectedPreparedBy () {
            this.selectedPreparedBy = null;
            this.model.prepared_by  = null;
        },
        onCheckedSearch(search) {
            if (search == '') {
                this.currentChecked = [];
                
                return false;
            }

            this.isCheckedLoading = true;
            this.searchChecked(search, this);
        },
        searchChecked: _.debounce((search, vm) => {
            let data = http.route('user/getall?filters[project_id]='+vm.$route.params.id+'&search_select=' + search).get();
            data.then(response => {
                if (response.data != false) {
                    vm.currentChecked = response.data;
                }
                vm.isCheckedLoading = false;
            });
        }, 500),
        updateChecked(user) {
            this.model.checked_id = (user != null) ? user.id : null;
        },
        clearSelectedChecked () {
            this.selectedChecked  = null;
            this.model.checked_id = null;
        },
        onApprovedSearch(search) {
            if (search == '') {
                this.currentApproved = [];
                
                return false;
            }

            this.isApprovedLoading = true;
            this.searchApproved(search, this);
        },
        searchApproved: _.debounce((search, vm) => {
            let data = http.route('user/getall?filters[project_id]='+vm.$route.params.id+'&search_select=' + search).get();
            data.then(response => {
                if (response.data != false) {
                    vm.currentApproved = response.data;
                }
                vm.isApprovedLoading = false;
            });
        }, 500),
        updateApproved(user) {
            this.model.approved_id = (user != null) ? user.id : null;
        },
        clearSelectedApproved () {
            this.selectedApproved  = null;
            this.model.approved_id = null;
        },
        onChangeOrderSearch(search) {
            if (search == '') {
                this.currentChangeOrder = [];
                
                return false;
            }

            this.isChangeOrderLoading = true;
            this.searchChangeOrder(search, this);
        },
        searchChangeOrder: _.debounce((search, vm) => {
            let data = http.route('change_order?project_id='+vm.$route.params.id+'&get_all=1&search_select=' + search + '&filters[parent_change_order_id]=null').get();
            data.then(response => {
                if (response.data != false) {
                    vm.currentChangeOrder = response.data;
                }
                vm.isChangeOrderLoading = false;
            });
        }, 500),
        updateChangeOrder(change_order) {
            this.model.change_order_id = (change_order != null) ? change_order.id : null;
        },
        clearSelectedChangeOrder () {
            this.selectedChangeOrder   = null;
            this.model.change_order_id = null;
        },
        onCompanySearch(search) {
            if (search == '') {
                this.currentCompany = [];
                
                return false;
            }

            this.isCompanyLoading = true;
            this.searchCompany(search, this);
        },
        searchCompany: _.debounce((search, vm) => {
            let data = http.route('company?get_all=1&search_select=' + search).get();
            data.then(response => {
                if (response.data != false) {
                    vm.currentCompany = response.data;
                }
                vm.isCompanyLoading = false;
            });
        }, 500),
        updateCompany(company) {
            this.model.execution_by_company = (company != null) ? company.id : null;
        },
        clearSelectedCompany () {
            this.selectedCompany            = null;
            this.model.execution_by_company = null;
        },
        onWorkingGroupTeamSearch(search) {
            if (search == '') {
                this.currentWorkingGroupTeam = [];
                
                return false;
            }

            this.isWorkingGroupTeamLoading = true;
            this.searchWorkingGroupTeam(search, this);
        },
        searchWorkingGroupTeam: _.debounce((search, vm) => {
            let data = http.route('team?project_id='+vm.$route.params.id+'&get_all=1&search_select=' + search).get();
            data.then(response => {
                if (response.data != false) {
                    vm.currentWorkingGroupTeam = response.data;
                }
                vm.isWorkingGroupTeamLoading = false;
            });
        }, 500),
        updateWorkingGroupTeam(team) {
            this.currentUser = [];
            this.clearSelectedUser();
            this.model.working_group_id = (team != null) ? team.id : null;

            if (team != null) {
                http.route('team/' + team.id +'/members?project_id='+this.$route.params.id).get().then(response => {
                    if (response.data != false) {
                        this.selectedUser = null;
                        this.currentUser = response.data;
                    }
                });
            }
        },
        clearSelectedWorkingGroupTeam () {
            this.selectedWorkingGroupTeam = null;
            this.model.working_group_id   = null;
            this.clearSelectedUser();
        },
        onUserSearch(search) {
            if (search == '') {
                this.currentUser = [];
                
                return false;
            }

            if (this.model.working_group_id != null) {
                this.isUserLoading = true;
                this.searchUser(search, this);
            }
        },
        searchUser: _.debounce((search, vm) => {
            let data = http.route('team/' + vm.model.working_group_id +'/members?project_id='+vm.$route.params.id+'&get_all=1&search_select=' + search).get();
            data.then(response => {
                if (response.data != false) {
                    vm.currentUser = response.data;
                }
                vm.isUserLoading = false;
            });
        }, 500),
        updateUser(user) {
            this.model.user_id = (user != null) ? user.user_id : null;
        },
        clearSelectedUser () {
            this.selectedUser  = null;
            this.model.user_id = null;
        },
    }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>


// WEBPACK FOOTER //
// src/components/pages/project/construction/site_instructions/site_instructions.vue